import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef, useState } from "react";

const AppContext = createContext(null);

const initialStore = {
  isUserSignedIn: false,
  user: null,
  shouldHideMenuNavigation: false,
  pastReviewsCurrentFilter: "current",
};

function reducer(state, action) {
  switch (action.type) {
    case "signInUser":
      return { ...state, isUserSignedIn: true, user: action.payload };
    case "signOutUser":
      return { ...state, isUserSignedIn: false, user: null };
    case "shouldHideMenuNavigation":
      return { ...state, shouldHideMenuNavigation: action.payload };
    case "updatePastReviewsFilter":
      return { ...state, pastReviewsCurrentFilter: action.payload };
    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialStore);
  const [toasts, setToasts] = useState([]);
  const toastHistoryRef = useRef(new Map());

  const commit = useCallback((type, payload) => dispatch({ type, payload }), []);

  const addToast = useCallback((toast) => {
    const severity = toast?.severity || "info";
    const summary = typeof toast?.summary === "string" ? toast.summary : "";
    const detail = typeof toast?.detail === "string" ? toast.detail : String(toast?.detail || "");
    const dedupeWindowMs = Number.isFinite(toast?.dedupeWindowMs) ? toast.dedupeWindowMs : 1500;
    const dedupeKey = toast?.dedupeKey || `${severity}|${summary}|${detail}`;
    const now = Date.now();
    const previousAt = toastHistoryRef.current.get(dedupeKey);
    if (previousAt && now - previousAt <= dedupeWindowMs) return;
    toastHistoryRef.current.set(dedupeKey, now);

    const id = `${Date.now()}-${Math.random()}`;
    setToasts((items) => [...items, { id, life: 5000, ...toast, severity, summary, detail }]);
    window.setTimeout(() => {
      setToasts((items) => items.filter((item) => item.id !== id));
    }, toast?.life || 5000);
  }, []);

  const value = useMemo(() => ({ state, commit, addToast }), [state, commit, addToast]);

  return (
    <AppContext.Provider value={value}>
      {children}
      <div className="toast-stack">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast-message toast-${toast.severity}`}>
            <strong>{toast.summary}</strong>
            <span>{toast.detail}</span>
          </div>
        ))}
      </div>
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used inside AppProvider");
  }
  return context;
}
